// apps/web/src/pages/vendas/detalhe/confirmacao-destrutiva-dialog.tsx — Fase 12, Plano 31 (PV-07, D-26, D-29; UI-SPEC § Destructive).
// Confirmação genérica das quatro ações destrutivas do PV (Cancelar item, Cancelar PV, Encerrar saldo, Trocar cor/material).
// O botão vermelho é reservado a elas; motivo obrigatório; spinner e botões travados durante a requisição;
// em erro o diálogo permanece aberto (mensagem inline + toast da página); conflito de versão oferece "Recarregar PV".
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { PedidoVendaView } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { MOTIVO_MINIMO_CARACTERES, motivoValido } from '../../../lib/vendas/ajustes';
import type { ResultadoComandoPv } from '../use-pv-comandos';
import { AlertaConflitoPv } from './alerta-conflito-pv';

interface ConfirmacaoDestrutivaDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly titulo: string;
  readonly corpo: string;
  /** Rótulo do botão destrutivo (verbo + objeto, igual ao título). */
  readonly rotuloConfirmar: string;
  /** Motivo já digitado em quem abriu o diálogo (ex.: Sheet de ajuste); o usuário ainda pode editar. */
  readonly motivoInicial?: string;
  /** Executa o comando com o motivo digitado (a versão do item/PV é resolvida por quem chama). */
  readonly onConfirmar: (motivo: string) => Promise<ResultadoComandoPv>;
  /** Chamado com o PV devolvido pela API quando o comando dá certo. */
  readonly onSucesso: (pv: PedidoVendaView) => void;
  /** Recarrega o PV (sem esconder a tela); devolve a versão atual ou null se a carga falhar. */
  readonly onRecarregar: () => Promise<PedidoVendaView | null>;
  /** Falha genérica: a página mostra o toast (o diálogo continua aberto). */
  readonly onFalha: (mensagem: string) => void;
}

export function ConfirmacaoDestrutivaDialog({
  aberto,
  onAbertoChange,
  titulo,
  corpo,
  rotuloConfirmar,
  motivoInicial = '',
  onConfirmar,
  onSucesso,
  onRecarregar,
  onFalha,
}: ConfirmacaoDestrutivaDialogProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [recarregando, setRecarregando] = useState(false);
  const [conflito, setConflito] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setMotivo(motivoInicial);
    setConflito(null);
    setErro(null);
  }, [aberto]);

  const podeConfirmar = motivoValido(motivo);

  async function confirmar(): Promise<void> {
    if (!podeConfirmar || enviando) return;
    setErro(null);
    setEnviando(true);
    try {
      const resultado = await onConfirmar(motivo.trim());
      if (resultado.tipo === 'ok') {
        setConflito(null);
        onSucesso(resultado.resultado);
        onAbertoChange(false);
      } else if (resultado.tipo === 'conflito') {
        setConflito(resultado.message);
      } else {
        setErro(resultado.message);
        onFalha(resultado.message);
      }
    } finally {
      setEnviando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      const atual = await onRecarregar();
      if (atual) {
        setConflito(null);
        setErro(null);
      } else {
        setErro('Não foi possível recarregar o PV. Tente novamente.');
      }
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{corpo}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="destrutivo-motivo">Motivo (obrigatório)</Label>
            <Textarea
              id="destrutivo-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              aria-describedby="destrutivo-motivo-ajuda"
              disabled={enviando}
              autoFocus
            />
            <p id="destrutivo-motivo-ajuda" className="text-xs text-muted-foreground">
              Informe ao menos {MOTIVO_MINIMO_CARACTERES} caracteres. O motivo fica registrado na auditoria.
            </p>
          </div>
          {erro && <FormMessage>{erro}</FormMessage>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" variant="destructive" onClick={() => void confirmar()} disabled={!podeConfirmar || enviando}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            {rotuloConfirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
